import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import BarPopup from './BarPopup';

const ColorBar = styled.div`
    position: relative;
    width: calc(100% / 24);
    background: ${(props) => props.color};
    height: 16px;
    display: inline-block;

    border-top-left-radius: ${(props) => (props.isAfterSameColor ? '0' : '10px')};
    border-bottom-left-radius: ${(props) => (props.isAfterSameColor ? '0' : '10px')};

    border-top-right-radius: ${(props) => (props.isBeforeSameColor ? '0' : '10px')};
    border-bottom-right-radius: ${(props) => (props.isBeforeSameColor ? '0' : '10px')};
    border: 1px solid ${(props) => props.color};

    ${(props) => props.color !== 'transparent' && css`
        cursor: pointer;
        :hover {
            filter: brightness(85%);
        }
    `}

        /* Manual time indicator */
    ${(props) => props.hasManual && css`
    position: relative;
    overflow: hidden;
    
    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.2),
        rgba(255, 255, 255, 0.2) 2px,
        transparent 2px,
        transparent 4px
      );
      pointer-events: none;
    }
  `}
`;

/* Labels sit right under the bar with no tick lines, like the prototype's
   ActivityTimeline (markers at top: 18px, 10px ink-500 text) */
const HourLine = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const HourLabel = styled.div`
    margin-top: 2px;
    position: absolute;
    color: #546077;
    font-size: 10px;
    line-height: 1.4;
    white-space: nowrap;
`;

function Bar({
  hourInfo, hour, isAfterSameColor, isBeforeSameColor,
}) {
  const hourLabel = (hour % 6 === 0) && (hour !== 0) ? ((hour % 12) || 12) : null;
  const AMPM = (hour >= 12 ? 'pm' : 'am');
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Check if any minute in this hour has manual entry
  const hasManual = Array.isArray(hourInfo)
    && hourInfo.some((min) => min.manual);

  const calculateActivityBarInfo = (hourInfo) => {
    const activityPercentage = Math.floor(
      (hourInfo.filter((min) => min.active).length / hourInfo.length) * 100,
    );

    let color = 'transparent';
    if (activityPercentage >= 80) {
      color = 'var(--green)';
    } else if (activityPercentage >= 60) {
      color = 'var(--green-2)';
    } else if (activityPercentage >= 40) {
      color = 'var(--yellow)';
    } else if (activityPercentage >= 20) {
      color = 'var(--orange)';
    } else if (activityPercentage > 0) {
      color = 'var(--red)';
    }

    return { color, activityPercentage };
  };

  return (
    <>
      <ColorBar
        color={Array.isArray(hourInfo) ? calculateActivityBarInfo(hourInfo).color : hourInfo.color}
        isAfterSameColor={isAfterSameColor}
        isBeforeSameColor={isBeforeSameColor}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        hasManual={hasManual}
      >
        {' '}
        {
          hourLabel && (
            <HourLine longLine={hourLabel}>
              <HourLabel className="size-xxs">{`${hourLabel}${AMPM}`}</HourLabel>
            </HourLine>
          )
        }
      </ColorBar>
      <BarPopup
        hour={hour}
        hourInfo={hourInfo}
        handlePopoverClose={handlePopoverClose}
        open={!!anchorEl}
        anchorEl={anchorEl}
      />
    </>
  );
}

// Helper function to calculate bar color (if not already calculated)

Bar.propTypes = {
  hourInfo: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string.isRequired,
    }),
    PropTypes.arrayOf(PropTypes.shape({
      active: PropTypes.bool,
      manual: PropTypes.bool,
    })),
  ]).isRequired,
  hour: PropTypes.number.isRequired,
  isAfterSameColor: PropTypes.bool.isRequired,
  isBeforeSameColor: PropTypes.bool.isRequired,
};

export default Bar;
